<!--
  A date field that always reads dd/mm/yyyy.

  WHY NOT `<input type="date">` ON ITS OWN. The browser renders that field in ITS OWN
  locale, which on a machine set to US English shows mm/dd/yyyy. There is no attribute
  or stylesheet that changes it — `lang` is not honoured for the rendered format. For a
  Belgian retailer reading 09/01 as 9 January when the browser means 1 September, that
  is not a cosmetic problem: it silently moves the promo window the whole result is
  measured over.

  The same is true of the first day of the week. `type="date"` starts its grid on
  whatever day the browser's locale says, which is Sunday on a US-English machine —
  and a retail week starts on Monday. There is no attribute for that either, so the
  calendar below is ours: a plain month grid, Monday first, no dependency.

  Typing is masked as you go (slashes inserted, digits only), and the value only
  leaves here as an ISO yyyy-mm-dd string, so nothing downstream has to know any of
  this happened.
-->
<template>
  <span class="df">
    <input
      ref="text"
      type="text"
      class="df-text"
      inputmode="numeric"
      autocomplete="off"
      placeholder="dd/mm/yyyy"
      maxlength="10"
      :aria-label="ariaLabel"
      :value="shown"
      @input="onInput"
      @blur="onBlur"
      @keydown.down.prevent="open = true"
    />
    <button type="button" class="df-btn" :class="{ 'df-btn-on': open }"
            @click="toggle"
            :aria-label="$t('controls.dateField.pick', { field: ariaLabel || $t('controls.dateField.thisField') })"
            :aria-expanded="open">
      <i class="bi bi-calendar3"></i>
    </button>

    <div v-if="open" ref="pop" class="df-pop" role="dialog"
         :aria-label="$t('controls.dateField.choose', { field: ariaLabel || $t('controls.dateField.thisField') })">
      <div class="df-nav">
        <button type="button" @click="shiftMonth(-1)" :aria-label="$t('controls.dateField.prevMonth')">
          <i class="bi bi-chevron-left"></i>
        </button>
        <b>{{ monthLabel }}</b>
        <button type="button" @click="shiftMonth(1)" :aria-label="$t('controls.dateField.nextMonth')">
          <i class="bi bi-chevron-right"></i>
        </button>
      </div>
      <!-- Monday first. The whole reason this calendar exists rather than the
           browser's own. -->
      <div class="df-dow">
        <span v-for="d in DOW" :key="d">{{ d }}</span>
      </div>
      <div class="df-grid">
        <button v-for="(c, i) in cells" :key="i" type="button"
                class="df-day" :data-iso="c.iso"
                :class="{ 'df-out': !c.inMonth, 'df-sel': c.iso === modelValue,
                          'df-today': c.iso === todayIso }"
                :disabled="c.disabled" @click="pick(c.iso)">
          {{ c.day }}
        </button>
      </div>
    </div>
  </span>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { formatWeekday } from '@/i18n/format.js'

const props = defineProps({
  /** ISO yyyy-mm-dd, or '' when empty. */
  modelValue: { type: String, default: '' },
  min: { type: String, default: null },
  max: { type: String, default: null },
  ariaLabel: { type: String, default: '' },
})
const emit = defineEmits(['update:modelValue'])

const text = ref(null)
const pop = ref(null)
/** Partially typed text lives here; a complete date lives in the model. */
const typing = ref(null)
const open = ref(false)

const { locale } = useI18n()
// Weekday heads in the portal language, Monday first: Mon…Sun / ma…zo.
// 1 January 2024 is a Monday.
const DOW = computed(() => Array.from({ length: 7 },
  (_, i) => formatWeekday(new Date(2024, 0, 1 + i), { short: true, lang: locale.value })))
const todayIso = new Date().toISOString().slice(0, 10)
/** Which month the grid is showing; follows the value, then the user's paging. */
const cursor = ref(null)

const toDisplay = (iso) => {
  if (!iso || iso.length < 10) return ''
  const [y, m, d] = iso.split('-')
  return `${d}/${m}/${y}`
}
const shown = computed(() => (typing.value === null ? toDisplay(props.modelValue) : typing.value))

/**
 * dd/mm/yyyy -> ISO, but only for a date that actually exists. 31/02 round-trips to
 * 2 March through the Date constructor, so the parts are compared back: a date that
 * changed under us was never a real one, and we would rather hold an empty value than
 * a silently different day.
 */
function toIso(str) {
  const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(str || '')
  if (!m) return null
  const [, dd, mm, yyyy] = m
  const dt = new Date(Date.UTC(+yyyy, +mm - 1, +dd))
  if (dt.getUTCFullYear() !== +yyyy || dt.getUTCMonth() !== +mm - 1
      || dt.getUTCDate() !== +dd) return null
  return `${yyyy}-${mm}-${dd}`
}

function onInput(e) {
  const digits = e.target.value.replace(/\D/g, '').slice(0, 8)
  let out = digits.slice(0, 2)
  if (digits.length > 2) out += `/${digits.slice(2, 4)}`
  if (digits.length > 4) out += `/${digits.slice(4, 8)}`
  typing.value = out
  // keep the caret sane: we rewrote the value, so put it back at the end
  e.target.value = out
  const iso = toIso(out)
  if (iso) emit('update:modelValue', iso)
  else if (!digits.length) emit('update:modelValue', '')
}

/** On leaving the field, an unfinished date is dropped rather than half-kept. */
function onBlur() {
  const iso = toIso(typing.value)
  if (typing.value !== null && !iso) emit('update:modelValue', '')
  typing.value = null
}

// ---------------------------------------------------------------------------
// the calendar
// ---------------------------------------------------------------------------

const iso = (d) => [d.getUTCFullYear(),
  String(d.getUTCMonth() + 1).padStart(2, '0'),
  String(d.getUTCDate()).padStart(2, '0')].join('-')

/** First of the month the grid shows: the current value, else today. */
function monthStart() {
  if (cursor.value) return cursor.value
  const base = props.modelValue ? new Date(props.modelValue) : new Date()
  return new Date(Date.UTC(base.getUTCFullYear
    ? base.getUTCFullYear() : base.getFullYear(),
  base.getUTCMonth ? base.getUTCMonth() : base.getMonth(), 1))
}

const monthLabel = computed(() => monthStart().toLocaleDateString(
  locale.value === 'nl' ? 'nl' : 'en-GB',
  { month: 'long', year: 'numeric', timeZone: 'UTC' }))

/**
 * Six weeks of cells, always, so the popup does not change height as you page
 * through months. `(getUTCDay() + 6) % 7` turns Sunday-first into Monday-first.
 */
const cells = computed(() => {
  const first = monthStart()
  const lead = (first.getUTCDay() + 6) % 7
  const out = []
  for (let i = 0; i < 42; i++) {
    const d = new Date(first.getTime())
    d.setUTCDate(1 - lead + i)
    const s = iso(d)
    out.push({
      iso: s,
      day: d.getUTCDate(),
      inMonth: d.getUTCMonth() === first.getUTCMonth(),
      disabled: (props.min && s < props.min) || (props.max && s > props.max),
    })
  }
  return out
})

function shiftMonth(n) {
  const m = monthStart()
  cursor.value = new Date(Date.UTC(m.getUTCFullYear(), m.getUTCMonth() + n, 1))
}

function toggle() {
  open.value = !open.value
  if (open.value) cursor.value = null      // reopen on the month of the value
}

function pick(s) {
  typing.value = null
  cursor.value = null
  open.value = false
  emit('update:modelValue', s)
}

/** Click-away and Escape, so the popup never strands the form. */
function onDocClick(e) {
  if (!open.value) return
  if (!text.value?.parentElement?.contains(e.target)) open.value = false
}
function onKey(e) {
  if (e.key === 'Escape') open.value = false
}
onMounted(() => {
  document.addEventListener('mousedown', onDocClick)
  document.addEventListener('keydown', onKey)
})
onBeforeUnmount(() => {
  document.removeEventListener('mousedown', onDocClick)
  document.removeEventListener('keydown', onKey)
})
</script>

<style scoped>
.df { position: relative; display: block; }
.df-text {
  width: 100%; padding: 8px 32px 8px 10px; border-radius: 8px; font-size: 13.5px;
  color: inherit; font-variant-numeric: tabular-nums;
  border: 1px solid var(--stora-border, #e6edf3); background: var(--stora-card, #fff);
}
.df-btn {
  position: absolute; top: 50%; right: 4px; transform: translateY(-50%);
  display: grid; place-items: center; width: 26px; height: 26px; padding: 0;
  border: 0; border-radius: 6px; background: transparent; cursor: pointer;
  color: var(--stora-muted, #6b7a86); font-size: 14px;
}
.df-btn:hover, .df-btn-on { background: var(--stora-bg, #f1f5f8); color: #38454f; }

/* ---- the month grid ---- */
.df-pop {
  position: absolute; z-index: 40; top: calc(100% + 5px); left: 0;
  /* never wider than the screen it opens on: on a phone the field can sit in a
     narrow grid cell, and a popup that runs off the edge cannot be used at all */
  width: 252px; max-width: calc(100vw - 28px); padding: 9px; border-radius: 10px;
  background: var(--stora-card, #fff); border: 1px solid var(--stora-border, #e6edf3);
  box-shadow: 0 10px 28px rgba(16, 32, 48, .14);
}
.df-nav {
  display: flex; align-items: center; justify-content: space-between;
  margin-bottom: 7px;
}
.df-nav b { font-size: 13px; color: #38454f; }
.df-nav button {
  display: grid; place-items: center; width: 24px; height: 24px; padding: 0;
  border: 0; border-radius: 6px; background: transparent; cursor: pointer;
  color: var(--stora-muted, #6b7a86); font-size: 13px;
}
.df-nav button:hover { background: var(--stora-bg, #f1f5f8); color: #38454f; }
.df-dow, .df-grid { display: grid; grid-template-columns: repeat(7, 1fr); gap: 1px; }
.df-dow span {
  text-align: center; padding-bottom: 4px; font-size: 9.5px; font-weight: 800;
  text-transform: uppercase; letter-spacing: .04em; color: #9aa7b2;
}
.df-day {
  height: 30px; min-width: 0; padding: 0; border: 0; border-radius: 6px; cursor: pointer;
  background: transparent; font-size: 12.5px; color: #38454f;
  font-variant-numeric: tabular-nums;
}
.df-day:hover:not(:disabled) { background: var(--stora-bg, #f1f5f8); }
.df-out { color: #c3ccd4; }
.df-today { font-weight: 800; box-shadow: inset 0 0 0 1px var(--stora-border, #e6edf3); }
.df-sel, .df-sel:hover:not(:disabled) {
  background: var(--stora-accent, #2f6f8f); color: #fff; font-weight: 700;
}
.df-day:disabled { color: #dde4ea; cursor: not-allowed; }
</style>
